import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, Signal, viewChild, WritableSignal, forwardRef, HostBinding, InputSignal, input, AfterViewInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteOrigin } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { IntersectionObserverDirective } from '@shared/directives/intersection-observer/intersection-observer.directive';
import { ObserveDirective } from '@shared/directives/intersection-observer/observe.directive';
import { ApiCollectionResponse } from '@shared/types/api/api-collection-response';
import { isEqual, isNull, isString } from 'es-toolkit';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, merge, Observable, skip, tap, throttleTime, withLatestFrom } from 'rxjs';
import { MatFormFieldControl } from '@angular/material/form-field';
import { LocaleConfig, TranslationStoreService } from '@ogs-gmbh/ngx-translate';
import { BaseSelectGetItems } from './types/base-select-get-items.type';
import { BaseSelectChange } from './types/base-select-change.type';
import { BaseSelectTouch } from './types/base-select-touch.type';
import { _ErrorStateTracker } from '@angular/material/core';
import { CustomFormFieldBase } from '@shared/types/custom-form-field-base.type';

const CONTROL_TYPE: string = "base-select";

@Component({
  selector: 'app-base-select',
  standalone: true,
  imports: [ MatInputModule, ReactiveFormsModule, MatAutocompleteModule, IntersectionObserverDirective, ObserveDirective ],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => BaseSelectComponent)
    }
  ],
  templateUrl: './base-select.component.html',
  styleUrl: './base-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseSelectComponent<T extends Record<string, unknown>> extends CustomFormFieldBase<T | null> implements ControlValueAccessor, MatFormFieldControl<T | null>, AfterViewInit {
  private readonly _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  public readonly controlType: string = CONTROL_TYPE;

  // TODO dont require access connected within component
  public readonly connectedFormField: InputSignal<MatAutocompleteOrigin> = input.required();

  public readonly getItems: InputSignal<BaseSelectGetItems<T>> = input.required();

  /**
   * Whether the select should reload the Items when Language was Changed.
   *
   * Defaults to `true`
   */
  public readonly shouldReloadOnLanguageChange: InputSignal<boolean> = input(true);


  private readonly _inputElement: Signal<ElementRef<HTMLInputElement>> = viewChild.required("baseInput");

  private readonly _searchTermChange$: BehaviorSubject<string> = new BehaviorSubject<string>("");

  // eslint-disable-next-line @unicorn/no-useless-undefined
  protected readonly items: WritableSignal<ApiCollectionResponse<T> | undefined> = signal(undefined);


  public readonly valueField: InputSignal<string> = input.required();

  public readonly identifierField: InputSignal<string> = input.required();

  public static nextId: number = 0;

  @HostBinding() id: string = `${ CONTROL_TYPE }-${ BaseSelectComponent.nextId++ }`;


  private _touched: boolean = false;

  private _focused: boolean = false;

  public get focused (): boolean {
    return this._focused;
  }

  @HostBinding('class.floating')
  public get shouldLabelFloat (): boolean {
    return this.focused || !this.empty;
  }

  autofilled?: boolean;

  userAriaDescribedBy?: string;

  disableAutomaticLabeling?: boolean;

  private _onChange: BaseSelectChange<T> | undefined = undefined;

  private _onTouch: BaseSelectTouch | undefined = undefined;


  constructor () {
    super();
    this.formControl = new FormControl<T | null>(null);

    if (this.ngControl)
      this.ngControl.valueAccessor = this;

    const shouldReloadOnLanguageChange$: Observable<boolean> = toObservable(this.shouldReloadOnLanguageChange);

    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((value: T | null) => {
        this._stateChange$.next();

        if (this._onChange)
          this._onChange(value);
      });
    merge(
      this._searchTermChange$.pipe(
        takeUntilDestroyed(this._destroyRef),
        skip(1),
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this._stateChange$.next())
      ),
      this._translationStoreService.getLocale$()
        .pipe(
          withLatestFrom(shouldReloadOnLanguageChange$),
          filter(([ _, shouldReloadOnLanguageChange ]: [LocaleConfig, boolean]) => shouldReloadOnLanguageChange),
          map(([ localeConfig, _ ]: [LocaleConfig, boolean]) => localeConfig),
          throttleTime(1000, undefined, { leading: true, trailing: true }),
          distinctUntilChanged(isEqual),
          takeUntilDestroyed(this._destroyRef)
        )
    )
      .subscribe(() => {
        this.loadItems(true);
      });
  }

  public ngAfterViewInit (): void {
    this.loadItems();
  }

  public setDescribedByIds (ids: string[]): void {
    this._inputElement().nativeElement.setAttribute('aria-describedby', ids.join(' '));
  }

  public onContainerClick (mouseEvent: MouseEvent): void {
    if ((mouseEvent.target as Element).tagName.toLowerCase() !== 'input')
      this._inputElement().nativeElement.focus();
  }

  protected searchItem (): void {
    const searchTerm: string = this._inputElement().nativeElement.value;

    this._searchTermChange$.next(searchTerm);
  }

  protected displayItem = (base: T | string | null): string => (isString(base) || isNull(base)
    ? base ?? ""
    : base[ this.valueField() ]) as string;


  protected loadItems (reset?: boolean): void {
    const getItems: BaseSelectGetItems<T> = this.getItems();
    const items: ApiCollectionResponse<T> | undefined = this.items();

    if (reset || items === undefined || items.totalCount > items.data.length) {
      getItems({
        offset: reset || !items?.data.length ? 0 : items.data.length,
        searchTerm: this._searchTermChange$.getValue() === "" ? undefined : this._searchTermChange$.getValue()
      })
        .subscribe((response: ApiCollectionResponse<T>) => {
          this.items.set({
            totalCount: response.totalCount,
            data: reset ? response.data : [ ...items?.data ?? [], ...response.data ]
          });
        });
    }
  }

  protected setFocusIn (): void {
    if (!this.focused) {
      this._focused = true;
      this._stateChange$.next();
    }
  }

  protected setFocusOut (focusEvent: FocusEvent): void {
    this._searchTermChange$.next("");

    if (!this._inputElement().nativeElement.contains(focusEvent.relatedTarget as Element)) {
      this._focused = false;
      this.markTouched();
      this._stateChange$.next();
    }
  }

  protected markTouched (): void {
    this._touched = true;

    if (this._onTouch)
      this._onTouch();
  }


  public registerOnChange (callback: BaseSelectChange<T>): void {
    this._onChange = callback;
  }

  registerOnTouched (callback: BaseSelectTouch): void {
    this._onTouch = callback;
  }
}
